import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateSocialMeta } from '../utils/seo';
import { Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy - Pin High Golf & Lounge';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Privacy Policy for Pin High Golf & Lounge. Learn how we collect, use, and protect your personal information.');
    }
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/privacy');
    updateSocialMeta({
      title: 'Privacy Policy - Pin High Golf & Lounge',
      description: 'Privacy Policy for Pin High Golf & Lounge. Learn how we collect, use, and protect your personal information.',
      path: '/privacy',
    });

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy',
      url: 'https://pinhighbay.com/privacy',
      isPartOf: { '@id': 'https://pinhighbay.com/#website' },
      dateModified: '2025-03-15',
    };
    let wpScript = document.querySelector('#privacy-webpage-schema');
    if (!wpScript) {
      wpScript = document.createElement('script');
      wpScript.id = 'privacy-webpage-schema';
      wpScript.type = 'application/ld+json';
      document.head.appendChild(wpScript);
    }
    wpScript.textContent = JSON.stringify(webPageSchema);

    return () => {
      const el = document.querySelector('#privacy-webpage-schema');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 to-green-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-green-300" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-lg text-green-50">Last updated: March 2025</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-lg max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
          <p className="text-gray-600 mb-6">
            Pin High Golf & Lounge ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services at our Bay Village, Ohio location.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Information We Collect</h2>
          <p className="text-gray-600 mb-4">We may collect information about you in various ways, including:</p>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li><strong>Personal Data:</strong> Name, email address, phone number, and mailing address when you make a reservation, sign up for membership, or contact us.</li>
            <li><strong>Payment Information:</strong> Credit card details and billing information when you make purchases (processed securely through our payment provider).</li>
            <li><strong>Reservation Data:</strong> Booking preferences, dates, and party size.</li>
            <li><strong>Usage Data:</strong> Information about how you use our website and services.</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">How We Use Your Information</h2>
          <p className="text-gray-600 mb-4">We use the information we collect to:</p>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li>Process and manage your reservations and memberships</li>
            <li>Send you confirmation emails and important updates about your bookings</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Send promotional communications (with your consent)</li>
            <li>Improve our website and services</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Information Sharing</h2>
          <p className="text-gray-600 mb-6">
            We do not sell, trade, or rent your personal information to third parties. We may share your information with trusted service providers who assist us in operating our website and conducting our business, subject to confidentiality agreements.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Data Security</h2>
          <p className="text-gray-600 mb-6">
            We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Cookies</h2>
          <p className="text-gray-600 mb-6">
            Our website may use cookies to enhance your experience. You can choose to disable cookies through your browser settings, but this may affect the functionality of our website.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Your Rights</h2>
          <p className="text-gray-600 mb-4">You have the right to:</p>
          <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your information (subject to legal requirements)</li>
            <li>Opt-out of marketing communications</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Children's Privacy</h2>
          <p className="text-gray-600 mb-6">
            Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Changes to This Policy</h2>
          <p className="text-gray-600 mb-6">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Contact Us</h2>
          <p className="text-gray-600 mb-4">If you have questions about this Privacy Policy, please contact us at:</p>
          <div className="bg-gray-50 p-6 rounded-lg text-gray-600">
            <p className="font-semibold text-gray-900">Pin High Golf & Lounge</p>
            <p>27229 Wolf Road</p>
            <p>Bay Village, OH 44140</p>
            <p>Phone: (440) 455-1078</p>
            <p>Email: dennis@pinhighbay.com</p>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <Link to="/" className="text-green-600 hover:text-green-700 font-medium">
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
